import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import {
  CreateInvoicePaymentInput,
  InvoicePaymentFieldsFragment,
  InvoicePaymentMethod,
  InvoicePaymentType,
} from "@/gql/graphql";
import dayjs from "dayjs";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  createInvoicePaymentMutation,
  updateInvoicePaymentMutation,
} from "@/app/Invoices/GraphQL/invoicePaymentMutations";
import {
  InvoiceRecord,
  invoiceRecordQuery,
} from "@/app/Invoices/GraphQL/invoiceRecordQuery";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { useFormikContext } from "formik";

type InvoicePaymentDialogProps = {
  invoiceId: string;
  type?: InvoicePaymentType;
  invoicePayment?: InvoicePaymentFieldsFragment;
};

export function InvoicePaymentDialog({
  invoiceId,
  invoicePayment,
  type = InvoicePaymentType.Payment,
}: InvoicePaymentDialogProps) {
  const { data } = useSuspenseGqlQuery(invoiceRecordQuery, {
    id: invoiceId,
  });
  const { invoice } = data;
  const { close } = useDialogState();

  const { mutateAsync } = useGqlMutation(createInvoicePaymentMutation);

  const { mutateAsync: updateInvoicePayment } = useGqlMutation(
    updateInvoicePaymentMutation,
  );

  const validation = z.object({
    amount: z
      .number({ error: "Enter an amount" })
      .min(1, { error: "Amount must be greater than 0" }),
    description: z.string().nullable().optional(),
    payment_date: z
      .string({ error: "Select a date" })
      .min(1, { error: "Select a date" }),
    type: z.enum(InvoicePaymentType),
  });

  return (
    <BaseDialog subtitle="Add payments, credits or refunds to an invoice">
      <GenericForm<CreateInvoicePaymentInput>
        validationSchema={toFormikValidationSchema(validation)}
        initialValues={{
          amount: invoicePayment?.amount ?? null!,
          description: invoicePayment?.description ?? "",
          payment_date: invoicePayment?.payment_date ?? "",
          invoice: { connect: invoice.id },
          type: invoicePayment?.type ?? type,
          method: invoicePayment?.method ?? InvoicePaymentMethod.DirectDeposit,
        }}
        onSubmit={async (values) => {
          if (!invoicePayment) {
            await mutateAsync({
              input: values,
            });
          } else {
            await updateInvoicePayment({
              id: invoicePayment.id,
              input: values,
            });
          }

          return close();
        }}
        className="grid grid-cols-3 gap-6 sm:grid-cols-12"
      >
        <FormContent invoice={invoice} invoicePayment={invoicePayment} />
      </GenericForm>
    </BaseDialog>
  );
}

function FormContent({
  invoice,
  invoicePayment,
}: {
  invoice: InvoiceRecord;
  invoicePayment?: InvoicePaymentFieldsFragment;
}) {
  const { values } = useFormikContext<CreateInvoicePaymentInput>();
  const paymentType = values.type;

  const labels: {
    [index in InvoicePaymentType]: {
      label: string;
      paymentDateLabel: string;
    };
  } = {
    [InvoicePaymentType.Payment]: {
      label: invoicePayment ? "Edit Payment" : "Add Payment",
      paymentDateLabel: "Paid on",
    },
    [InvoicePaymentType.Credit]: {
      label: invoicePayment ? "Edit Credit" : "Issue Credit",
      paymentDateLabel: "Credited on",
    },
    [InvoicePaymentType.Refund]: {
      label: invoicePayment ? "Edit Refund" : "Issue Refund",
      paymentDateLabel: "Refunded on",
    },
  };

  return (
    <>
      <PaymentTypeToggle />
      <CurrencyInput
        name="amount"
        label="Amount"
        currency={invoice.currency}
        autoFocus
      />
      <DateInput
        name="payment_date"
        label={labels[paymentType].paymentDateLabel}
        max={dayjs().format("YYYY-MM-DD")}
      />
      <TextInput
        name="description"
        label="Reference"
        className="col-span-4"
        optionalLabel
      />
      <RemainingAmount invoice={invoice} />
      <DialogSubmitButton text={labels[paymentType].label} />
    </>
  );
}

function PaymentTypeToggle() {
  const { values, setFieldValue } =
    useFormikContext<CreateInvoicePaymentInput>();

  const types = [
    { value: InvoicePaymentType.Payment, label: "Payment" },
    { value: InvoicePaymentType.Credit, label: "Credit" },
    { value: InvoicePaymentType.Refund, label: "Refund" },
  ];

  return (
    <div className="col-span-12">
      <div className="flex gap-2 rounded-lg bg-gray-100 p-1">
        {types.map((type) => (
          <button
            key={type.value}
            type="button"
            onClick={() => setFieldValue("type", type.value)}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              values.type === type.value
                ? "bg-white text-gray-900 shadow"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {type.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function RemainingAmount({ invoice }: { invoice: InvoiceRecord }) {
  return (
    <>
      <div className="col-span-2 text-right">
        <dt className="text-sm font-bold text-gray-900">Amount due</dt>
        <dd className="mt-3 text-sm text-gray-900">
          {formatCurrency(invoice.amount_due, invoice.currency, {
            showZero: true,
            allowNegative: true,
          })}
        </dd>
      </div>
    </>
  );
}
